<script>
import SpellCheckedText from "@/components/SpellCheckedText.vue";
import PhotoBox from "@/components/PhotoBox.vue";

export default {
    name: "CPTemp7",
    components: {
        SpellCheckedText,
        PhotoBox
    },
    props: {
        projects: { type: Object, required: true },
        pageGap: { type: Boolean, default: true },
        startPage: { type: Number, default: 1 },
    },
    computed: {
        projectPages() {
            const pages = [];
            if (this.projects && this.projects.projects) {
                for (let i = 0; i < this.projects.projects.length; i += 2) {
                    pages.push(this.projects.projects.slice(i, i + 2));
                }
            }
            return pages;
        }
    }
};
</script>

<template>
    <div class="main-container" :style="{
        gap: pageGap ? '20px' : '0px',
        '--header-color': projects.style.colorScheme.header,
        '--body-color': projects.style.colorScheme.body,
        '--footer-color': projects.style.colorScheme.footer,
        '--heading-color': projects.style.heading.color,
        '--heading-size': projects.style.heading.size + 'px',
        '--heading-font': projects.style.heading.font,
        '--heading-weight': projects.style.heading.bold ? 'bold' : 'normal',
        '--heading-style': projects.style.heading.italic ? 'italic' : 'normal',
        '--heading-decoration': projects.style.heading.underline ? 'underline' : 'none',
        '--label-color': projects.style.label.color,
        '--label-size': projects.style.label.size + 'px',
        '--label-font': projects.style.label.font,
        '--label-weight': projects.style.label.bold ? 'bold' : 'normal',
        '--label-style': projects.style.label.italic ? 'italic' : 'normal',
        '--label-decoration': projects.style.label.underline ? 'underline' : 'none',
        '--text-color': projects.style.text.color,
        '--text-size': projects.style.text.size + 'px',
        '--text-font': projects.style.text.font,
        '--text-weight': projects.style.text.bold ? 'bold' : 'normal',
        '--text-style': projects.style.text.italic ? 'italic' : 'normal',
        '--text-decoration': projects.style.text.underline ? 'underline' : 'none',
    }">
        <div v-for="(page, pageIndex) in projectPages" :key="pageIndex" class="sub-container page-unit">
            <!-- HEADER -->
            <div class="header-banner banner">
                <div class="header-content">
                    <h2 class="header-title">
                        <SpellCheckedText :value="projects.title" @input="projects.title = $event" />
                    </h2>
                    <div class="header-logo photobox">
                        <PhotoBox :project="projects" type="logo" bgColor="transparent" objectFit="contain" />
                    </div>
                </div>
            </div>

            <div class="page-content">
                <div v-for="(project, pIdx) in page" :key="pIdx" class="project-item">
                    <div class="grid-section">
                        <div class="left-col">
                            <div class="detail-section">
                                <div class="title-section">
                                    <h1 class="project-name">
                                        <SpellCheckedText :value="project.projectName || 'N/A'"
                                            @input="project.projectName = $event" />
                                    </h1>
                                    <p class="location">
                                        <SpellCheckedText :value="project.city || 'N/A'"
                                            @input="project.city = $event" />,
                                        <SpellCheckedText :value="project.stateProvince || 'N/A'"
                                            @input="project.stateProvince = $event" />
                                    </p>
                                </div>

                                <div class="team-and-features">
                                    <div class="team-section" v-if="project && project.projectTeam && project.projectTeam.length > 0">
                                        <p class="section-title">Project Team</p>
                                        <div class="team-list">
                                            <div v-for="(member, mIdx) in (project.projectTeam || [])" :key="mIdx"
                                                class="team-member">
                                                <div class="avatar">
                                                    <PhotoBox class="photobox" :project="member"
                                                        :bgColor="projects.style.colorScheme.body" type="profile" />
                                                </div>
                                                <div class="member-info">
                                                    <p class="member-name">
                                                        <SpellCheckedText :value="member.name"
                                                            @input="member.name = $event" />
                                                        -
                                                    </p>
                                                    <p class="member-role">
                                                        <SpellCheckedText :value="member.role"
                                                            @input="member.role = $event" />
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="features-section" v-if="project && project.keyFeatures && project.keyFeatures.length > 0">
                                        <p class="section-title">Key Features</p>
                                        <ul class="features-list">
                                            <li v-for="(feature, fIdx) in project.keyFeatures || []" :key="fIdx">
                                                <SpellCheckedText :value="feature" @input="feature = $event" />
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div class="small-photos">
                                <div class="small-photo-container photobox">
                                    <PhotoBox :project="project" :index="2"
                                        :bgColor="projects.style.colorScheme && projects.style.colorScheme.body" />
                                </div>
                                <div class="small-photo-container photobox">
                                    <PhotoBox :project="project" :index="3"
                                        :bgColor="projects.style.colorScheme && projects.style.colorScheme.body" />
                                </div>
                            </div>
                        </div>

                        <div class="right-col">
                            <div class="big-photo-container photobox">
                                <PhotoBox :project="project" :index="0" :bgColor="projects.style.colorScheme && projects.style.colorScheme.body" />
                            </div>
                            <div class="big-photo-container photobox">
                                <PhotoBox :project="project" :index="1" :bgColor="projects.style.colorScheme && projects.style.colorScheme.body" />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Empty slot to maintain layout for odd numbered projects -->
                <div v-if="page.length === 1" class="project-item empty-item"></div>
            </div>

            <!-- FOOTER -->
            <div class="footer-bottom">
                <div class="footer-content">
                    <div class="footer-left">
                        <div class="footer-line" />
                        <p class="footer-text">
                            <SpellCheckedText :value="projects.opprName" @input="projects.opprName = $event" />
                        </p>
                    </div>
                    <div class="footer-right">
                        <div class="page-badge">Page {{ startPage + pageIndex }}</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.main-container {
    display: flex;
    flex-direction: column;
    width: 211mm;
    min-height: 296.83mm;
    position: relative;
    box-sizing: border-box;
    background: #f5f5f5;

    &:hover {
        .photobox :deep(.photo-overlay) {
            opacity: 1;
        }

        .photobox :deep(img) {
            opacity: 0.9;
        }
    }

    .sub-container {
        display: flex;
        flex-direction: column;
        background: white;
        height: 296.83mm;
        width: 211mm;
        position: relative;
        overflow: hidden;
        padding-bottom: 40px;
    }

    .banner {
        height: 100px;
        min-height: 100px;
        display: flex;
        align-items: center;
        padding: 0 50px;

        &.header-banner {
            background: var(--header-color);
        }
    }

    .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;

        .header-title {
            color: white;
            font-size: 16px;
            font-weight: 700;
            margin: 0;
            font-family: Helvetica;
        }

        .header-logo {
            height: 80px;
            width: 120px;
        }
    }

    .page-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        padding: 20px 40px;
        gap: 20px;
    }

    .project-item {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    .grid-section {
        display: flex;
        gap: 5px;
        height: 100%;
        width: 100%;
    }

    .left-col {
        flex: 1.3;
        display: flex;
        flex-direction: column;
        gap: 5px;
        overflow: hidden;

        .detail-section {
            height: 320px;
            overflow: hidden;

            .title-section {
                .project-name {
                    color: var(--heading-color);
                    font-size: var(--heading-size);
                    font-weight: var(--heading-weight);
                    font-family: var(--heading-font);
                    font-style: var(--heading-style);
                    text-decoration: var(--heading-decoration);
                    margin: 0;
                }

                .location {
                    color: var(--label-color);
                    font-size: var(--label-size);
                    font-weight: var(--label-weight);
                    font-family: var(--label-font);
                    font-style: var(--label-style);
                    text-decoration: var(--label-decoration);
                    margin-bottom: 14px;
                }
            }

            .team-and-features {
                display: flex;
                gap: 20px;
                flex: 1;
                overflow: hidden;

                .team-section,
                .features-section {
                    flex: 1;
                    overflow: hidden;
                }

                .section-title {
                    color: var(--heading-color);
                    font-size: var(--heading-size);
                    font-weight: var(--heading-weight);
                    font-family: var(--heading-font);
                    font-style: var(--heading-style);
                    text-decoration: var(--heading-decoration);
                    margin-bottom: 8px;
                }

                .team-list {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;

                    .team-member {
                        display: flex;
                        align-items: center;
                        gap: 10px;

                        .avatar {
                            width: 35px;
                            height: 35px;
                            border-radius: 50%;
                            overflow: hidden;
                            background: #eee;
                            flex-shrink: 0;

                            &:hover {
                                .photobox :deep(.photo-overlay) {
                                    gap: 0px;
                                }

                                .photobox :deep(.overlay-btn) {
                                    width: 15px;
                                    height: 15px;
                                }
                            }
                        }

                        .member-info {
                            font-size: 11px;
                            line-height: 1.2;

                            .member-name {
                                color: var(--text-color);
                                font-size: calc(var(--text-size) - 2px);
                                font-weight: var(--text-weight);
                                font-family: var(--text-font);
                                font-style: var(--text-style);
                                text-decoration: var(--text-decoration);
                                margin-bottom: 0px;
                            }

                            .member-role {
                                color: var(--text-color);
                                font-size: calc(var(--text-size) - 2px);
                                font-weight: var(--text-weight);
                                font-family: var(--text-font);
                                font-style: var(--text-style);
                                text-decoration: var(--text-decoration);
                                margin-bottom: 0px;
                            }
                        }
                    }
                }

                .features-list {
                    margin: 0;
                    padding-left: 20px;
                    list-style-type: disc;

                    li {
                        color: var(--text-color);
                        font-size: var(--text-size);
                        font-weight: var(--text-weight);
                        font-family: var(--text-font);
                        font-style: var(--text-style);
                        text-decoration: var(--text-decoration);
                        margin-bottom: 3px;
                    }
                }
            }
        }

        .small-photos {
            display: flex;
            gap: 5px;
            height: 120px;
            margin-top: auto;

            .small-photo-container {
                flex: 1;
                background: #eee;
                border-radius: 4px;
                overflow: hidden;
            }
        }
    }

    .right-col {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 5px;

        .big-photo-container {
            flex: 1;
            background: #eee;
            border-radius: 4px;
            overflow: hidden;
            max-height: 220px;
            min-height: 220px;
        }
    }

    .footer-bottom {
        margin-top: auto;

        .footer-content {
            display: flex;
            justify-content: space-between;
            align-items: center;

            .footer-left {
                flex: 1;
                margin: 0 20px 0px 50px;

                .footer-line {
                    height: 2px;
                    background: var(--footer-color);
                }

                .footer-text {
                    margin-top: 10px;
                    margin-bottom: 0px;
                    font-size: 14px;
                    color: black;
                    font-family: Helvetica;
                }
            }

            .footer-right {
                .page-badge {
                    background: var(--footer-color);
                    color: white;
                    padding: 8px 80px 8px 30px;
                    font-size: 12px;
                    font-weight: 700;
                }
            }
        }
    }
}
</style>
